<template>
  <div class="link-page">
    <!-- Top Bar -->
    <header class="bol-header">
      <div class="header-left">
        <span class="header-logo">🔗</span>
        <h1 class="header-title">Vincular Contacto</h1>
      </div>
      <div class="header-right">
        <router-link to="/boleteria" class="nav-link">🎫 Boletería</router-link>
        <button @click="logout" class="logout-btn">Salir</button>
      </div>
    </header>

    <div v-if="!token" class="auth-redirect">
      <p>Sesión no iniciada</p>
      <router-link :to="'/admin?redirect=/boleteria/link'" class="login-link">Iniciar sesión →</router-link>
    </div>

    <main v-else class="link-content">
      <!-- Step 1: Scan / Enter UUID -->
      <div v-if="step === 'scan'" class="step-card">
        <h2 class="card-title">📱 Escanear ticket</h2>
        <p class="card-desc">Escaneá el QR del ticket para vincular datos del espectador</p>

        <div id="link-qr-reader" class="qr-reader"></div>

        <div class="manual-row">
          <input
            v-model="manualUUID"
            class="form-input"
            placeholder="O ingresá el UUID manualmente..."
            @keyup.enter="lookupTicket(manualUUID)"
          >
          <button @click="lookupTicket(manualUUID)" class="action-btn" :disabled="!manualUUID">
            Buscar
          </button>
        </div>
        <p v-if="error" class="error-msg">{{ error }}</p>
      </div>

      <!-- Step 2: Show ticket + contact form -->
      <div v-if="step === 'form'" class="step-card">
        <!-- Ticket info -->
        <div class="ticket-banner">
          <div class="ticket-banner-func">{{ ticketInfo.funcion_name }}</div>
          <div class="ticket-banner-meta">
            {{ paymentLabel(ticketInfo.payment_method) }} — ${{ ticketInfo.price }} UYU
            — <span :class="'status-' + ticketInfo.status">{{ statusLabel(ticketInfo.status) }}</span>
          </div>
        </div>

        <!-- Existing contact warning -->
        <div v-if="ticketInfo.has_contact" class="existing-contact">
          <p>⚠️ Este ticket ya tiene datos vinculados:</p>
          <p class="existing-name">{{ ticketInfo.contact.first_name }} {{ ticketInfo.contact.last_name }}</p>
          <p class="existing-email" v-if="ticketInfo.contact.email">{{ ticketInfo.contact.email }}</p>
          <p class="existing-note">Completar el formulario actualizará los datos.</p>
        </div>

        <!-- Contact form -->
        <h2 class="card-title">👤 Datos del espectador</h2>
        <div class="form-row">
          <div class="form-group half">
            <label class="form-label">Nombre *</label>
            <input v-model="contact.first_name" class="form-input" placeholder="Nombre" autofocus>
          </div>
          <div class="form-group half">
            <label class="form-label">Apellido *</label>
            <input v-model="contact.last_name" class="form-input" placeholder="Apellido">
          </div>
        </div>
        <div class="form-row">
          <div class="form-group half">
            <label class="form-label">Email</label>
            <input v-model="contact.email" type="email" class="form-input" placeholder="email@ejemplo.com">
          </div>
          <div class="form-group half">
            <label class="form-label">Teléfono</label>
            <input v-model="contact.phone" class="form-input" placeholder="099...">
          </div>
        </div>
        <div class="checkbox-row">
          <label class="checkbox-label">
            <input type="checkbox" v-model="contact.wants_news">
            <span>Recibir novedades</span>
          </label>
          <label class="checkbox-label">
            <input type="checkbox" v-model="contact.wants_membership">
            <span>Interés en membresía</span>
          </label>
        </div>

        <!-- Consent notice for RedTickets -->
        <div v-if="ticketInfo && ticketInfo.payment_method === 'redtickets'" class="consent-notice">
          <p class="consent-title">📋 Consentimiento</p>
          <p class="consent-text">Este ticket fue comprado por RedTickets. ¿El espectador autoriza el uso de sus datos para recibir información de Grupo Babilonia?</p>
        </div>

        <div class="form-actions">
          <button @click="resetToScan" class="cancel-btn">← Otro ticket</button>
          <button
            @click="linkContact"
            class="action-btn"
            :disabled="!contact.first_name || !contact.last_name || saving"
          >
            {{ saving ? 'Guardando...' : 'Vincular' }}
          </button>
        </div>
        <p v-if="error" class="error-msg">{{ error }}</p>
      </div>

      <!-- Step 3: Success -->
      <div v-if="step === 'success'" class="step-card success-card">
        <div class="success-icon">✅</div>
        <h2 class="success-title">¡Vinculado!</h2>
        <p class="success-name">{{ lastLinked.first_name }} {{ lastLinked.last_name }}</p>
        <p class="success-func">{{ ticketInfo.funcion_name }}</p>
        <button @click="resetToScan" class="action-btn">Escanear otro →</button>
      </div>

      <!-- Link counter -->
      <div class="counter-card">
        <span class="counter-value">{{ linkCount }}</span>
        <span class="counter-label">contactos vinculados</span>
      </div>
    </main>
  </div>
</template>

<script>
const API_BASE = '/api'

export default {
  name: 'BoleteriaLink',
  data() {
    return {
      token: sessionStorage.getItem('admin_token'),
      step: 'scan', // scan → form → success
      scanner: null,
      manualUUID: '',
      currentUUID: '',
      ticketInfo: null,
      contact: {
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        wants_news: false,
        wants_membership: false,
      },
      saving: false,
      error: '',
      lastLinked: null,
      linkCount: 0,
    }
  },
  async mounted() {
    if (!this.token) return
    await this.initScanner()
  },
  beforeUnmount() {
    this.stopScanner()
  },
  methods: {
    async initScanner() {
      if (!window.Html5Qrcode) {
        await new Promise((resolve) => {
          const script = document.createElement('script')
          script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html5-qrcode/2.3.8/html5-qrcode.min.js'
          script.onload = resolve
          script.onerror = resolve
          document.head.appendChild(script)
        })
      }

      if (!window.Html5Qrcode) return

      try {
        this.scanner = new window.Html5Qrcode('link-qr-reader')
        await this.scanner.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 250, height: 250 } },
          (decodedText) => this.onScan(decodedText),
          () => {}
        )
      } catch (err) {
        console.error('Scanner error:', err)
      }
    },

    stopScanner() {
      if (this.scanner) {
        try { this.scanner.stop() } catch { /* ignore */ }
      }
    },

    async onScan(uuid) {
      if (this.step !== 'scan') return
      this.stopScanner()
      await this.lookupTicket(uuid)
    },

    async lookupTicket(uuid) {
      if (!uuid) return
      uuid = uuid.trim()
      this.error = ''

      try {
        const res = await fetch(`${API_BASE}/tickets/info/${uuid}`, {
          headers: { 'Authorization': `Bearer ${this.token}` }
        })
        if (res.status === 401) { this.logout(); return }
        if (!res.ok) {
          this.error = 'Ticket no encontrado'
          return
        }

        this.ticketInfo = await res.json()
        this.currentUUID = uuid
        this.manualUUID = ''

        // Pre-fill contact if exists
        if (this.ticketInfo.contact) {
          this.contact = {
            first_name: this.ticketInfo.contact.first_name || '',
            last_name: this.ticketInfo.contact.last_name || '',
            email: this.ticketInfo.contact.email || '',
            phone: this.ticketInfo.contact.phone || '',
            wants_news: this.ticketInfo.contact.wants_news || false,
            wants_membership: this.ticketInfo.contact.wants_membership || false,
          }
        } else {
          this.contact = {
            first_name: '', last_name: '', email: '', phone: '',
            wants_news: false, wants_membership: false,
          }
        }

        this.step = 'form'
        this.stopScanner()
      } catch {
        this.error = 'Error de conexión'
      }
    },

    async linkContact() {
      this.error = ''
      this.saving = true

      try {
        const res = await fetch(`${API_BASE}/tickets/link/${this.currentUUID}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.token}`,
          },
          body: JSON.stringify(this.contact)
        })

        if (res.status === 401) { this.logout(); return }

        const data = await res.json()
        if (res.ok) {
          this.lastLinked = data.contact
          this.linkCount++
          this.step = 'success'
          if (navigator.vibrate) navigator.vibrate(200)
        } else {
          this.error = data.error || 'Error al vincular'
        }
      } catch {
        this.error = 'Error de conexión'
      }
      this.saving = false
    },

    async resetToScan() {
      this.step = 'scan'
      this.ticketInfo = null
      this.currentUUID = ''
      this.error = ''
      this.contact = {
        first_name: '', last_name: '', email: '', phone: '',
        wants_news: false, wants_membership: false,
      }
      // Reinit scanner after DOM updates
      this.$nextTick(() => this.initScanner())
    },

    paymentLabel(method) {
      return { cash: 'Efectivo', transfer: 'Transferencia', redtickets: 'RedTickets', invitation: 'Invitación', combo: 'Combo', '2x1': '2 por 1' }[method] || method
    },
    statusLabel(status) {
      return { valid: 'Válido', used: 'Utilizado', cancelled: 'Cancelado' }[status] || status
    },

    logout() {
      sessionStorage.removeItem('admin_token')
      this.$router.push('/admin?redirect=/boleteria/link')
    }
  }
}
</script>

<style scoped>
.link-page {
  min-height: 100vh;
  background: #0a0a0a;
  color: #e0e0e0;
}

/* Header */
.bol-header {
  display: flex; justify-content: space-between; align-items: center;
  padding: 12px 24px; background: #111; border-bottom: 1px solid #222;
  flex-wrap: wrap; gap: 10px;
}
.header-left { display: flex; align-items: center; gap: 10px; }
.header-logo { font-size: 28px; }
.header-title { font-size: 20px; color: #c9a227; margin: 0; }
.header-right { display: flex; align-items: center; gap: 10px; }
.nav-link {
  color: #888; text-decoration: none; font-size: 13px;
  padding: 6px 10px; border-radius: 6px; transition: all 0.2s;
}
.nav-link:hover { color: #c9a227; background: #1a1a1a; }
.logout-btn {
  background: #333; color: #aaa; border: none; padding: 6px 14px;
  border-radius: 6px; cursor: pointer; font-size: 13px;
}
.auth-redirect { text-align: center; padding: 100px 20px; color: #666; }
.login-link { display: inline-block; margin-top: 15px; color: #c9a227; text-decoration: none; }

/* Content */
.link-content {
  max-width: 500px;
  margin: 0 auto;
  padding: 20px 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.step-card {
  background: #111;
  border: 1px solid #222;
  border-radius: 12px;
  padding: 24px 20px;
}
.card-title {
  font-size: 18px;
  color: #c9a227;
  margin: 0 0 16px;
}
.card-desc {
  color: #666;
  font-size: 14px;
  margin: -8px 0 16px;
}

/* Scanner */
.qr-reader {
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 14px;
}
.manual-row {
  display: flex;
  gap: 10px;
}
.form-input {
  flex: 1;
  padding: 12px 14px;
  background: #0a0a0a;
  border: 1px solid #333;
  border-radius: 8px;
  color: #fff;
  font-size: 14px;
  outline: none;
  box-sizing: border-box;
}
.form-input:focus { border-color: #c9a227; }

/* Ticket banner */
.ticket-banner {
  background: #0a0a0a;
  border: 1px solid #333;
  border-radius: 10px;
  padding: 16px;
  text-align: center;
  margin-bottom: 20px;
}
.ticket-banner-func {
  font-size: 18px;
  font-weight: bold;
  color: #c9a227;
  margin-bottom: 6px;
}
.ticket-banner-meta {
  font-size: 13px;
  color: #888;
}
.status-valid { color: #2ecc71; }
.status-used { color: #f39c12; }
.status-cancelled { color: #e74c3c; }

/* Existing contact */
.existing-contact {
  background: #1a1500;
  border: 1px solid #c9a22744;
  border-radius: 8px;
  padding: 14px;
  margin-bottom: 20px;
  font-size: 14px;
}
.existing-contact p { margin: 0 0 4px; }
.existing-name { font-weight: bold; color: #c9a227; }
.existing-email { color: #888; }
.existing-note { color: #666; font-size: 12px; font-style: italic; }

/* Form fields */
.form-row { display: flex; gap: 12px; }
.form-group { margin-bottom: 14px; }
.form-group.half { flex: 1; }
.form-label {
  display: block; font-size: 12px; color: #888;
  margin-bottom: 4px; text-transform: uppercase; letter-spacing: 0.05em;
}

.checkbox-row {
  display: flex; gap: 20px; flex-wrap: wrap; margin-bottom: 18px;
}
.checkbox-label {
  display: flex; align-items: center; gap: 8px;
  color: #aaa; font-size: 14px; cursor: pointer;
}
.checkbox-label input[type="checkbox"] {
  accent-color: #c9a227; width: 16px; height: 16px;
}

/* Actions */
.form-actions {
  display: flex;
  gap: 12px;
  justify-content: space-between;
}
.action-btn {
  padding: 12px 24px;
  background: #c9a227;
  color: #000;
  font-weight: bold;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 15px;
  transition: all 0.2s;
}
.action-btn:hover { background: #e0b830; }
.action-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.cancel-btn {
  padding: 12px 20px;
  background: #222;
  border: 1px solid #333;
  color: #aaa;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
}
.cancel-btn:hover { background: #333; color: #fff; }

.error-msg {
  color: #e74c3c; text-align: center; margin-top: 10px; font-size: 14px;
}

/* Success */
.success-card { text-align: center; }
.success-icon { font-size: 56px; margin-bottom: 10px; }
.success-title { font-size: 24px; color: #2ecc71; margin: 0 0 10px; }
.success-name { font-size: 18px; color: #fff; margin: 0 0 4px; }
.success-func { font-size: 14px; color: #c9a227; margin: 0 0 20px; }

/* Consent notice for RedTickets */
.consent-notice {
  background: #1a1500;
  border: 1px solid #c9a22744;
  border-radius: 8px;
  padding: 14px;
  margin-bottom: 18px;
}
.consent-title {
  font-size: 14px;
  font-weight: bold;
  color: #c9a227;
  margin: 0 0 6px;
}
.consent-text {
  font-size: 13px;
  color: #aaa;
  margin: 0;
  line-height: 1.4;
}

/* Counter */
.counter-card {
  text-align: center; padding: 20px;
  background: #111; border: 1px solid #222; border-radius: 12px;
}
.counter-value { display: block; font-size: 48px; font-weight: bold; color: #c9a227; }
.counter-label { font-size: 14px; color: #666; text-transform: uppercase; }

@media (max-width: 480px) {
  .link-content { padding: 12px 8px; }
  .form-row { flex-direction: column; gap: 0; }
  .form-actions { flex-direction: column-reverse; }
}
</style>